/**
 * Wheel Config Editor (Phase 7.0 - PNG Hybrid Mode)
 * 
 * With PNG-based wheel, visual structure is FIXED:
 * - Number of segments: 6 (baked into PNG)
 * - Segment colors: Fixed in PNG
 * - Configurable: Labels, Icons, Outcome Keys, Weights (probability)
 */

import { useState, useCallback, useEffect } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Select,
  Text,
  Divider,
  Box,
  Banner,
  ButtonGroup,
  Badge,
  Collapsible,
  Checkbox,
} from "@shopify/polaris";
import type { WheelSegment, WheelConfig } from "../games/runtime/types";
import { ICON_OPTIONS } from "../games/wheel_of_fortune/iconLoader";
import type { IconKey } from "../games/runtime/types";

// Fixed segment count for PNG-based wheel
const FIXED_SEGMENT_COUNT = 9;

// Fixed colors from PNG (for display only, not editable)
// These approximate the actual PNG segment colors
const FIXED_SEGMENT_COLORS = [
  "#E63946", // Red
  "#1D3557", // Dark Blue
  "#2A9D8F", // Teal
  "#E9C46A", // Yellow/Gold
  "#E76F51", // Orange/Coral
  "#457B9D", // Blue
  "#F4A261", // Light Orange
  "#264653", // Dark Teal
  "#A8DADC", // Light Cyan
];

interface WheelConfigEditorProps {
  config: WheelConfig;
  onChange: (config: WheelConfig) => void;
  rewardPolicyId?: string; // Optional: For fetching available outcomes
}

export function WheelConfigEditor({ config, onChange, rewardPolicyId }: WheelConfigEditorProps) {
  // Saved vs Draft state
  const [savedConfig, setSavedConfig] = useState<WheelConfig>(config);
  const [draftConfig, setDraftConfig] = useState<WheelConfig>(config);
  const [isSaving, setIsSaving] = useState(false);
  
  // Fetch available outcomes from reward policy
  const [availableOutcomes, setAvailableOutcomes] = useState<Array<{value: string; label: string}>>([]);
  
  useEffect(() => {
    async function fetchOutcomes() {
      if (!rewardPolicyId) {
        setAvailableOutcomes([]);
        return;
      }
      
      try {
        // Fetch reward policy to get available outcomes
        const response = await fetch(`/api/reward-policies/${rewardPolicyId}`);
        if (!response.ok) throw new Error('Failed to fetch policy');
        
        const policy = await response.json();
        
        // Extract outcomes from policy
        const outcomes = (policy.config?.outcomes || []).map((outcome: any) => ({
          value: outcome.outcomeKey,
          label: `${outcome.outcomeKey} - ${outcome.outcomeType} ${
            outcome.payload?.value ? `(${outcome.payload.value}${outcome.payload.valueType === 'percentage' ? '%' : ''})` : ''
          }`.trim()
        }));
        
        setAvailableOutcomes(outcomes);
      } catch (error) {
        console.error('[WheelConfig] Failed to fetch outcomes:', error);
        setAvailableOutcomes([]);
      }
    }
    
    fetchOutcomes();
  }, [rewardPolicyId]);
  
  // Ensure we always have exactly FIXED_SEGMENT_COUNT segments
  const ensureFixedSegments = useCallback((segments: WheelSegment[]): WheelSegment[] => {
    const result: WheelSegment[] = [];
    for (let i = 0; i < FIXED_SEGMENT_COUNT; i++) {
      if (segments[i]) {
        result.push({
          ...segments[i],
          color: FIXED_SEGMENT_COLORS[i], // Always use fixed color
        });
      } else {
        // Fill missing segments with defaults
        result.push({
          outcomeKey: `outcome_${i + 1}`,
          label: `Prize ${i + 1}`,
          color: FIXED_SEGMENT_COLORS[i],
          weight: 1,
          iconKey: 'none',
        });
      }
    }
    return result;
  }, []);

  // Initialize segments properly
  const [segments, setSegments] = useState<WheelSegment[]>(() => 
    ensureFixedSegments(draftConfig.segments)
  );
  const [spinDuration, setSpinDuration] = useState(String(draftConfig.spinDuration || 4000));
  const [ctaText, setCtaText] = useState(draftConfig.ctaText || "Spin to Win!");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(0);
  
  // Check if config is dirty (has unsaved changes)
  const isDirty = JSON.stringify(draftConfig) !== JSON.stringify(savedConfig);

  // Update draft config (preview changes live, but not saved)
  const updateDraftConfig = useCallback((updates: Partial<WheelConfig>) => {
    const newConfig = { ...draftConfig, ...updates };
    setDraftConfig(newConfig);
    // Update preview immediately
    onChange(newConfig);
  }, [draftConfig, onChange]);
  
  // Save config (persist to backend)
  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      setValidationError(null);
      
      setSavedConfig(draftConfig);
      console.log("[WheelConfig] Saved (in-memory):", draftConfig);
      // TODO: Show success toast
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  }, [draftConfig]);
  
  // Reset to last saved config
  const handleReset = useCallback(() => {
    setDraftConfig(savedConfig);
    setSegments(ensureFixedSegments(savedConfig.segments));
    setSpinDuration(String(savedConfig.spinDuration || 4000));
    setCtaText(savedConfig.ctaText || "Spin to Win!");
    onChange(savedConfig); // Update preview
    setValidationError(null);
  }, [savedConfig, onChange, ensureFixedSegments]);

  // Update segment (limited fields - no color changes!)
  const handleUpdateSegment = useCallback((index: number, field: keyof WheelSegment, value: string | number | boolean) => {
    const newSegments = [...segments];
    
    // Block color changes - PNG colors are fixed
    if (field === 'color') {
      console.warn('[WheelConfig] Color changes not supported in PNG mode');
      return;
    }
    
    if (field === 'weight') {
      const numValue = typeof value === 'number' ? value : parseInt(value as string);
      if (numValue < 1 || numValue > 100) {
        setValidationError("Weight must be between 1 and 100");
        return;
      }
      newSegments[index] = { ...newSegments[index], weight: numValue };
    } else if (field === 'label') {
      const strValue = String(value);
      if (strValue.length > 30) {
        setValidationError("Label max 30 characters");
        return;
      }
      newSegments[index] = { ...newSegments[index], label: strValue };
    } else if (field === 'outcomeKey') {
      newSegments[index] = { ...newSegments[index], outcomeKey: String(value) };
    } else if (field === 'iconKey') {
      newSegments[index] = { ...newSegments[index], iconKey: value as IconKey };
    } else if (field === 'iconEnabled') {
      newSegments[index] = { ...newSegments[index], iconEnabled: Boolean(value) };
    } else if (field === 'iconColor') {
      newSegments[index] = { ...newSegments[index], iconColor: String(value) };
    } else if (field === 'iconSize') {
      const numValue = typeof value === 'number' ? value : parseFloat(value as string);
      newSegments[index] = { ...newSegments[index], iconSize: numValue };
    } else if (field === 'iconOffset') {
      const numValue = typeof value === 'number' ? value : parseFloat(value as string);
      newSegments[index] = { ...newSegments[index], iconOffset: numValue };
    }

    setSegments(newSegments);
    updateDraftConfig({ segments: newSegments });
    setValidationError(null);
  }, [segments, updateDraftConfig]);

  // Generate segment selector options
  const segmentOptions = segments.map((seg, idx) => ({
    label: `Segment ${idx + 1} - ${seg.label}`,
    value: String(idx),
  }));

  return (
    <BlockStack gap="400">
      {/* Save/Reset Actions + Unsaved Badge */}
      <Card>
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="300" blockAlign="center">
            <Text as="h2" variant="headingMd" fontWeight="semibold">
              Wheel Configuration
            </Text>
            {isDirty && (
              <Badge tone="attention">Unsaved changes</Badge>
            )}
          </InlineStack>
          
          <ButtonGroup>
            <Button
              onClick={handleReset}
              disabled={!isDirty || isSaving}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              loading={isSaving}
            >
              Save
            </Button>
          </ButtonGroup>
        </InlineStack>
        
        {isDirty && (
          <Box paddingBlockStart="300">
            <Text as="p" variant="bodySm" tone="subdued">
              Preview shows your edits. Click Save to apply permanently.
            </Text>
          </Box>
        )}
      </Card>

      {/* PNG Mode Notice */}
      <Banner tone="info">
        <Text as="p" variant="bodySm">
          <strong>PNG Hybrid Mode:</strong> This wheel uses premium PNG assets. 
          Segment count ({FIXED_SEGMENT_COUNT}) and colors are fixed. 
          You can customize labels, icons, and reward outcomes.
        </Text>
      </Banner>

      {/* Validation Banner */}
      {validationError && (
        <Banner tone="warning" onDismiss={() => setValidationError(null)}>
          {validationError}
        </Banner>
      )}

      {/* Segments - Fixed count, no add/remove */}
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd" fontWeight="semibold">
              Segments ({FIXED_SEGMENT_COUNT} fixed)
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Configure labels, icons, and reward outcomes for each segment
            </Text>
          </BlockStack>

          <Divider />

          {/* Segment Selector Dropdown */}
          <Select
            label="Select segment to edit"
            options={segmentOptions}
            value={String(selectedSegmentIndex)}
            onChange={(value) => setSelectedSegmentIndex(parseInt(value))}
          />

          {/* Show only selected segment */}
          {segments[selectedSegmentIndex] && (
            <SegmentEditorPNG
              segment={segments[selectedSegmentIndex]}
              index={selectedSegmentIndex}
              fixedColor={FIXED_SEGMENT_COLORS[selectedSegmentIndex]}
              availableOutcomes={availableOutcomes}
              onUpdate={handleUpdateSegment}
            />
          )}
        </BlockStack>
      </Card>

      {/* Wheel Settings */}
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd" fontWeight="semibold">
            Wheel Settings
          </Text>

          <TextField
            label="Spin Duration"
            type="number"
            value={spinDuration}
            onChange={(value) => {
              setSpinDuration(value);
              const duration = parseInt(value) || 4000;
              if (duration >= 1000 && duration <= 10000) {
                updateDraftConfig({ spinDuration: duration });
              }
            }}
            suffix="ms"
            autoComplete="off"
            min="1000"
            max="10000"
            helpText="How long the wheel spins (1-10 seconds)"
          />

          <TextField
            label="Button Text"
            value={ctaText}
            onChange={(value) => {
              setCtaText(value);
              updateDraftConfig({ ctaText: value });
            }}
            autoComplete="off"
            maxLength={20}
            helpText="Text shown on center button"
          />
        </BlockStack>
      </Card>
    </BlockStack>
  );
}

/**
 * Segment Editor for PNG Mode (no color editing)
 */
interface SegmentEditorPNGProps {
  segment: WheelSegment;
  index: number;
  fixedColor: string;
  availableOutcomes: Array<{value: string; label: string}>;
  onUpdate: (index: number, field: keyof WheelSegment, value: string | number | boolean) => void;
}

function SegmentEditorPNG({ segment, index, fixedColor, availableOutcomes, onUpdate }: SegmentEditorPNGProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Icon options for Select dropdown
  const iconSelectOptions = ICON_OPTIONS.map(opt => ({
    label: opt.label,
    value: opt.value,
  }));
  
  // Outcome options with fallback if no policy selected
  const outcomeOptions = availableOutcomes.length > 0
    ? availableOutcomes
    : [{ value: segment.outcomeKey, label: `${segment.outcomeKey} (no policy selected)` }];

  return (
    <Box padding="400" background="bg-surface-secondary" borderRadius="200">
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h3" variant="headingSm" fontWeight="medium">
              Segment {index + 1}
            </Text>
            {/* Fixed color indicator */}
            <div
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: fixedColor,
                borderRadius: '4px',
                border: '2px solid #e1e3e5',
              }}
              title={`Fixed color: ${fixedColor}`}
            />
          </InlineStack>
          <Badge tone="info">Color fixed</Badge>
        </InlineStack>

        <Select
          label="Reward Outcome"
          options={outcomeOptions}
          value={segment.outcomeKey}
          onChange={(value) => onUpdate(index, 'outcomeKey', value)}
          helpText={availableOutcomes.length === 0 
            ? "⚠️ Select a Reward Policy in campaign to see available outcomes"
            : "Reward that will be granted when this segment wins"}
        />

        <TextField
          label="Label"
          value={segment.label}
          onChange={(value) => onUpdate(index, 'label', value)}
          autoComplete="off"
          maxLength={30}
          showCharacterCount
          helpText="Text displayed on segment"
        />

        <TextField
          label="Weight (Probability)"
          type="number"
          value={String(segment.weight)}
          onChange={(value) => onUpdate(index, 'weight', parseInt(value) || 1)}
          autoComplete="off"
          min={1}
          max={100}
          helpText="Higher weight = higher chance to win this prize"
        />

        {/* Icon Configuration */}
        <Divider />
        <BlockStack gap="300">
          <InlineStack gap="400" blockAlign="end">
            <Box minWidth="180px">
              <Select
                label="Icon"
                options={iconSelectOptions}
                value={segment.iconKey || 'none'}
                onChange={(value) => onUpdate(index, 'iconKey', value)}
              />
            </Box>
            
            <Checkbox
              label="Show Icon"
              checked={segment.iconEnabled !== false}
              onChange={(checked) => onUpdate(index, 'iconEnabled', checked)}
            />
          </InlineStack>

          {/* Advanced Icon Settings (Collapsible) */}
          <Button
            onClick={() => setAdvancedOpen(!advancedOpen)}
            disclosure={advancedOpen ? 'up' : 'down'}
            size="slim"
            variant="plain"
          >
            Advanced icon settings
          </Button>
          
          <Collapsible
            open={advancedOpen}
            id={`icon-advanced-${index}`}
            transition={{ duration: '150ms', timingFunction: 'ease-in-out' }}
          >
            <Box paddingBlockStart="200">
              <BlockStack gap="300">
                <Select
                  label="Icon Color"
                  options={[
                    { label: 'Auto (contrast)', value: 'auto' },
                    { label: 'White', value: '#FFFFFF' },
                    { label: 'Black', value: '#000000' },
                    { label: 'Gold', value: '#FFD700' },
                  ]}
                  value={segment.iconColor || 'auto'}
                  onChange={(value) => onUpdate(index, 'iconColor', value)}
                  helpText="Auto picks high-contrast color"
                />

                <InlineStack gap="400">
                  <Box minWidth="120px">
                    <TextField
                      label="Icon Size"
                      type="number"
                      value={String(segment.iconSize ?? 0.12)}
                      onChange={(value) => onUpdate(index, 'iconSize', parseFloat(value) || 0.12)}
                      autoComplete="off"
                      helpText="0.05 - 0.30"
                    />
                  </Box>

                  <Box minWidth="120px">
                    <TextField
                      label="Icon Position"
                      type="number"
                      value={String(segment.iconOffset ?? 0.55)}
                      onChange={(value) => onUpdate(index, 'iconOffset', parseFloat(value) || 0.55)}
                      autoComplete="off"
                      helpText="0.3 - 0.95 (center to edge)"
                    />
                  </Box>
                </InlineStack>
              </BlockStack>
            </Box>
          </Collapsible>
        </BlockStack>

      </BlockStack>
    </Box>
  );
}
